import { useEffect, useRef, useState } from "react";

import { captureException, captureMessage } from "@sentry/react-native";
import * as ImagePicker from "expo-image-picker";
import { some } from "lodash";
import { Pressable, View } from "react-native";
import { ActivityIndicator, Badge, useTheme } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import Image from "./Image";
import ImageCropper, { CropperImage } from "./ImageCropper";
import useSnackbar from "../contexts/SnackbarContext";

export type UploadImage = {
  url: string;
  base64?: string;
};

export type ImageStorageProps = {
  uploadImageToStorage: (base64: string) => Promise<{ imageUrl: string }>;
  deleteImageFromStorage: (url: string) => Promise<void>;
};

type Props = ImageStorageProps & {
  images: UploadImage[];
  addImage: (image: UploadImage) => void;
  removeImage: (index: number) => void;
  replaceImage: (index: number, image: UploadImage) => void;
};

export default function ImageUpload({
  images,
  addImage,
  removeImage,
  replaceImage,
  uploadImageToStorage,
  deleteImageFromStorage,
}: Props) {
  const [editingImage, setEditingImage] = useState<CropperImage | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  const imagesPendingUpload = useRef<CropperImage[]>([]);

  const { showMessage } = useSnackbar();

  // Shows image upload btn as loading while image is being uploaded.
  const [isLoading, setIsLoading] = useState(false);
  const { colors } = useTheme();
  const tailwind = useTailwind();

  // When the images array changes due to an image being scheduled for upload
  // upload any new images.
  useEffect(() => {
    // Uploads an image to digital ocean and replaces the image in the state
    // Also removes from pending upload array.
    async function uploadImage(image: CropperImage, index: number) {
      if (!image.base64) return;

      const imagesIndex = images.findIndex((i) => i.base64 === image.base64);

      try {
        const { imageUrl } = await uploadImageToStorage(image.base64);
        // If the image was already in the state, replace it with the new url.
        if (imagesIndex !== -1) {
          replaceImage(imagesIndex, { url: imageUrl, base64: image.base64 });
        }
      } catch (e) {
        captureException(e);
        // Delete failed images from state.
        if (imagesIndex !== -1) {
          removeImage(imagesIndex);
        }
      } finally {
        // Delete image from pending upload array to signal we are done with it.
        imagesPendingUpload.current = imagesPendingUpload.current.filter(
          (_, i) => i !== index
        );
      }
    }

    imagesPendingUpload.current.forEach(uploadImage);
  }, [images, removeImage, replaceImage, uploadImageToStorage]);

  // Opens the image picker and sets the selected image as the editing image.
  async function pickImageFromLibrary() {
    setIsLoading(true);

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        quality: 0.8,
        base64: true,
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
      });

      if (result.canceled) {
        return setIsLoading(false);
      }

      if (some(images, (img) => img.base64 === result.assets[0].base64)) {
        showMessage("imagen ya existe en el producto");
        return setIsLoading(false);
      }

      setTimeout(() => setEditingImage(result.assets[0]), 1000);
    } catch (e) {
      showMessage("error cargando imagen");
      captureException(e);
      setIsLoading(false);
    }
  }

  // Add image to state and to upload queue.
  async function scheduleImageUpload(image: CropperImage) {
    if (!image.base64) {
      captureMessage("Image picker failed on product photo selection");
      stopEditingImage();
      return;
    }
    imagesPendingUpload.current.push(image);
    addImage({ url: "", base64: image.base64 });
    stopEditingImage();
  }

  function stopEditingImage() {
    setIsLoading(false);
    setEditingImage(null);
  }

  function deleteImage(index: number) {
    removeImage(index);
    // Wrap jic deletion fails.
    // No actionable if this fails bc removeImage already removed from state.
    try {
      const imageUrl = images[index]?.url;
      deleteImageFromStorage(imageUrl);
    } catch (error) {
      captureException(error, { extra: { imageUrl: images[index]?.url } });
    }
  }

  return (
    <View style={tailwind("flex-row flex-wrap")}>
      {editingImage && (
        <ImageCropper
          image={editingImage}
          fixedAspectRatio={
            aspectRatio || editingImage.width / editingImage.height
          }
          onEditingCancel={stopEditingImage}
          onEditingComplete={scheduleImageUpload}
          setAspectRatio={setAspectRatio}
        />
      )}

      {isLoading ? (
        <Pressable style={tailwind("mr-3 mt-3")}>
          <ActivityIndicator
            style={[
              { backgroundColor: colors.surfaceDisabled },
              tailwind("p-3 rounded-md"),
            ]}
            color={colors.onSurfaceDisabled}
            size={36}
          />
        </Pressable>
      ) : (
        <Pressable onPress={pickImageFromLibrary} style={tailwind("mr-3 mt-3")}>
          <Image style={tailwind("rounded-md")} placeholder="plus" />
        </Pressable>
      )}

      {images.map(({ url, base64 }, index) => (
        <View key={index} style={tailwind("mr-3 mt-3 relative")}>
          <Image src={url} base64={base64} style={tailwind("rounded-md")} />
          <Badge
            style={tailwind("absolute -top-2.5 -right-2.5")}
            onPress={() => deleteImage(index)}
          >
            ✕
          </Badge>
        </View>
      ))}
    </View>
  );
}
